import React, { useState, useEffect, useRef } from 'react';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, storage, getEffectiveUserId } from '../firebase';
import Menu from '../main/menu';
import './styles/billedearkiv.css';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useCustomerSwitch } from '../contexts/CustomerSwitchContext';
import { useLanguage } from '../contexts/LanguageContext';

const Billedearkiv = ({ onLogout }) => {
  const { selectedCustomer, isWorkingOnBehalfOfCustomer } = useCustomerSwitch();
  const { t } = useLanguage();
  const [images, setImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [notification, setNotification] = useState(null);
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  // Hent billeder ved indlæsning eller når selectedCustomer ændres
  useEffect(() => {
    if (auth.currentUser) {
      fetchImages();
    }
  }, [selectedCustomer]); // Tilføj selectedCustomer som afhængighed

  // Opsæt drag-and-drop event listeners
  useEffect(() => {
    const dropArea = dropAreaRef.current;
    
    if (dropArea) {
      const preventDefault = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
      
      const highlight = () => {
        dropArea.classList.add('active');
      };
      
      const unhighlight = () => {
        dropArea.classList.remove('active');
      };
      
      const handleDrop = (e) => {
        preventDefault(e);
        unhighlight();
        
        const dt = e.dataTransfer;
        const files = dt.files;
        
        handleFiles(files);
      };
      
      dropArea.addEventListener('dragenter', preventDefault);
      dropArea.addEventListener('dragover', preventDefault);
      dropArea.addEventListener('dragleave', unhighlight);
      dropArea.addEventListener('drop', handleDrop);
      
      dropArea.addEventListener('dragenter', highlight);
      dropArea.addEventListener('dragover', highlight);
      
      return () => {
        dropArea.removeEventListener('dragenter', preventDefault);
        dropArea.removeEventListener('dragover', preventDefault);
        dropArea.removeEventListener('dragleave', unhighlight);
        dropArea.removeEventListener('drop', handleDrop);
        
        dropArea.removeEventListener('dragenter', highlight);
        dropArea.removeEventListener('dragover', highlight);
      };
    }
  }, []);

  // Hent billeder fra Firebase Storage
  const fetchImages = async () => {
    try {
      // Brug getEffectiveUserId for at få den korrekte bruger-ID
      const userId = getEffectiveUserId(selectedCustomer);
      
      if (!userId) {
        console.error("Ingen gyldig bruger-ID fundet");
        return;
      }
      
      console.log("Henter billeder for bruger:", userId, selectedCustomer ? "Arbejder på vegne af kunde" : "Egen bruger");
      
      // Hent metadata fra Firestore
      const imagesQuery = query(
        collection(db, 'users', userId, 'images')
      );
      
      const querySnapshot = await getDocs(imagesQuery);
      const imagesData = [];
      
      querySnapshot.forEach((doc) => {
        imagesData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setImages(imagesData);
    } catch (error) {
      console.error('Fejl ved hentning af billeder:', error);
      showNotification('error', t('imageArchive', 'notification.generalError'));
    }
  };

  // Håndter fil-valg fra input
  const handleFileInputChange = (e) => {
    handleFiles(e.target.files);
  };

  // Håndter valgte filer
  const handleFiles = (files) => {
    if (files.length === 0) return;
    
    const newFiles = Array.from(files).map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type
    }));
    
    // Kontroller at der kun er billedfiler
    const validFiles = newFiles.filter(file => file.type.startsWith('image/'));
    
    if (validFiles.length !== newFiles.length) {
      showNotification('error', t('imageArchive', 'messages.onlyImageFiles'));
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  // Upload valgte filer
  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;
    
    setIsUploading(true);
    // Brug getEffectiveUserId for at få den korrekte bruger-ID
    const userId = getEffectiveUserId(selectedCustomer);
    
    if (!userId) {
      console.error("Ingen gyldig bruger-ID fundet");
      setIsUploading(false);
      showNotification('error', t('imageArchive', 'messages.noValidUserId'));
      return;
    }
    
    console.log("Uploader billeder for bruger:", userId, selectedCustomer ? "Arbejder på vegne af kunde" : "Egen bruger");
    
    let uploadedCount = 0;
    
    try {
      for (const fileObj of selectedFiles) {
        const file = fileObj.file;
        const fileName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, `users/${userId}/images/${fileName}`);
        
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => {
              console.error('Upload fejl:', error);
              reject(error);
            },
            async () => {
              // Upload komplet
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                
                // Gem metadata i Firestore
                const docRef = await addDoc(collection(db, 'users', userId, 'images'), {
                  name: file.name,
                  fileName: fileName,
                  storageRef: `users/${userId}/images/${fileName}`,
                  downloadURL: downloadURL,
                  contentType: file.type,
                  size: file.size,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                  uploadedAt: new Date()
                });
                
                // Upload billede til Telegram og gemme file_id
                await uploadImageToTelegram(downloadURL, userId, docRef.id);
                
                uploadedCount++;
                resolve();
              } catch (error) {
                reject(error);
              }
            }
          );
        });
      }
      
      // Reset filvalg og fremgang
      setSelectedFiles([]);
      setUploadProgress(0);
      setIsUploading(false);
      
      // Opdater listen
      fetchImages();
      
      showNotification(
        'success', 
        t('imageArchive', 'notification.uploadSuccess', { count: uploadedCount })
      );
    } catch (error) {
      console.error('Fejl ved upload:', error);
      setIsUploading(false);
      showNotification('error', t('imageArchive', 'notification.generalError'));
    }
  };

  // Ny funktion til at uploade billede til Telegram og gemme file_id
  const uploadImageToTelegram = async (imageUrl, userId, imageId) => {
    try {
      // Tjek først om brugeren har et tilknyttet Telegram chatId
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        console.log('Brugeren eksisterer ikke i databasen');
        return; // Afslut tidligt hvis bruger ikke findes
      }
      
      const userData = userDoc.data();
      
      // Hvis brugeren ikke har en telegramChatId eller telegramConnected ikke er true, spring over Telegram upload
      if (!userData.telegramChatId || userData.telegramConnected !== true) {
        console.log(`Brugeren ${userId} har ikke tilknyttet Telegram, springer upload over`);
        showNotification('info', t('imageArchive', 'notification.uploadTelegramFail'));
        return; // Afslut funktionen tidligt - billedet er stadig gemt i Firestore og Storage
      }
      
      // Brug Cloud Functions til at uploade billedet til Telegram
      console.log(`Sender anmodning om at uploade billede til Telegram via Cloud Functions: ${imageId}`);
      
      const functionUrl = 'https://us-central1-app-nuvik.cloudfunctions.net/uploadImageToTelegram';
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl: imageUrl,
          userId: userId,
          imageId: imageId
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Cloud Function fejl: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log('Billede uploadet til Telegram via Cloud Function, file_id:', result.telegram_file_id);
        
        // Telegram file_id gemmes automatisk af Cloud Function
        showNotification('success', t('imageArchive', 'notification.uploadTelegramSuccess'));
      } else {
        throw new Error(result.error || 'Ukendt fejl ved upload af billede til Telegram');
      }
    } catch (error) {
      console.error('Fejl ved upload til Telegram:', error);
      showNotification('warning', t('imageArchive', 'notification.uploadTelegramFail'));
    }
  };

  // Slet et billede
  const deleteImage = async (image) => {
    if (!window.confirm(t('imageArchive', 'gallery.deleteConfirm', { name: image.name }))) {
      return;
    }
    
    try {
      // Brug getEffectiveUserId for at få den korrekte bruger-ID
      const userId = getEffectiveUserId(selectedCustomer);
      
      if (!userId) {
        console.error("Ingen gyldig bruger-ID fundet");
        showNotification('error', t('imageArchive', 'notification.deleteFail'));
        return;
      }
      
      // Konstruer storage path hvis storageRef mangler eller er ugyldig
      let storagePath = image.storageRef;
      if (!storagePath || storagePath === '/' || storagePath === '') {
        // Konstruer storage path baseret på bruger-ID og filnavn
        if (image.fileName) {
          storagePath = `users/${userId}/images/${image.fileName}`;
        } else if (image.name) {
          storagePath = `users/${userId}/images/${image.name}`;
        } else {
          console.error("Kan ikke konstruere storage path - ingen filnavn fundet");
          showNotification('error', t('imageArchive', 'notification.deleteFail'));
          return;
        }
      }
      
      console.log(`Sletter billede fra storage path: ${storagePath}`);
      
      // Opret reference til billedet
      const imageRef = ref(storage, storagePath);
      
      try {
        // Slet fra Storage
        await deleteObject(imageRef);
        console.log('Billede slettet fra Storage');
      } catch (storageError) {
        // Hvis billedet ikke findes i Storage, fortsæt med at slette fra Firestore
        console.warn('Billede ikke fundet i Storage eller allerede slettet:', storageError.message);
      }
      
      // Slet fra Firestore
      await deleteDoc(doc(db, 'users', userId, 'images', image.id));
      console.log('Billede slettet fra Firestore');
      
      // Opdater listen
      setImages(images.filter(img => img.id !== image.id));
      
      showNotification('success', t('imageArchive', 'notification.deleteSuccess'));
    } catch (error) {
      console.error('Fejl ved sletning af billede:', error);
      showNotification('error', t('imageArchive', 'notification.deleteFail'));
    }
  };

  // Kopier URL til udklipsholder
  const copyImageUrl = (url) => {
    navigator.clipboard.writeText(url)
      .then(() => {
        showNotification('success', t('imageArchive', 'notification.copySuccess'));
      })
      .catch((error) => {
        console.error('Fejl ved kopiering:', error);
        showNotification('error', t('imageArchive', 'notification.copyFail'));
      });
  };

  // Vis besked
  const showNotification = (type, message) => {
    setNotification({ type, message });
    
    // Fjern besked efter 5 sekunder
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  // Fjern en fil fra listen inden upload
  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Konverter filstørrelse til læsbar format
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Åbn filbrowser
  const openFileBrowser = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="dashboard-layout">
      <Menu onLogout={onLogout} />
     
      <div className="dashboard-content">
        {/* Vis banner, hvis man arbejder på vegne af kunde */}
        {isWorkingOnBehalfOfCustomer && (
          <div className="customer-action-banner">
            <i className="fas fa-user-friends"></i>
            <span>{t('imageArchive', 'customerBanner', { customerName: selectedCustomer.name || selectedCustomer.email })}</span>
          </div>
        )}
      
        <div className="ba-page-header">
          <h1>{t('imageArchive', 'header.title')}</h1>
          <p className="ba-page-description">
            {t('imageArchive', 'header.description')}
          </p>
        </div>
        
        <div className="ba-container">
          <div className="ba-card">
            <div 
              ref={dropAreaRef}
              className="ba-upload-area"
              onClick={openFileBrowser}
            >
              <i className="fas fa-cloud-upload-alt ba-upload-icon"></i>
              <p className="ba-upload-text">{t('imageArchive', 'upload.dragDrop')}</p>
              <p className="ba-upload-subtext">{t('imageArchive', 'upload.clickUpload')}</p>
              
              <input 
                type="file" 
                ref={fileInputRef}
                className="ba-file-input"
                accept="image/*"
                multiple
                onChange={handleFileInputChange}
              />
            </div>
            
            {selectedFiles.length > 0 && (
              <div className="ba-upload-file-list">
                {selectedFiles.map((fileObj, index) => (
                  <div key={index} className="ba-upload-file-item">
                    <i className="fas fa-image ba-file-icon"></i>
                    <div className="ba-file-info">
                      <div className="ba-file-name">{fileObj.name}</div>
                      <div className="ba-file-size">{formatFileSize(fileObj.size)}</div>
                    </div>
                    <button 
                      className="ba-remove-file"
                      onClick={() => removeSelectedFile(index)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
                
                {isUploading && (
                  <div className="ba-upload-progress">
                    <div 
                      className="ba-upload-progress-bar"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}
                
                <button 
                  className="ba-button ba-button-primary"
                  onClick={uploadFiles}
                  disabled={isUploading}
                >
                  <i className="fas fa-upload"></i>
                  {isUploading ? t('imageArchive', 'upload.uploading') : t('imageArchive', 'upload.uploadButton')}
                </button>
              </div>
            )}
          </div>
          
          <div className="ba-card">
            <h2>{t('imageArchive', 'gallery.title')}</h2>
            
            {images.length > 0 ? (
              <div className="ba-gallery">
                {images.map((image) => (
                  <div key={image.id} className="ba-image-item">
                    <img src={image.downloadURL} alt={image.name} />
                    <div className="ba-image-name">{image.name}</div>
                    <div className="ba-image-actions">
                      <button 
                        className="ba-image-action-button copy"
                        onClick={() => copyImageUrl(image.downloadURL)}
                        title={t('imageArchive', 'gallery.imageActions.copy')}
                      >
                        <i className="fas fa-link"></i>
                      </button>
                      <button 
                        className="ba-image-action-button delete"
                        onClick={() => deleteImage(image)}
                        title={t('imageArchive', 'gallery.imageActions.delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ba-empty-gallery">
                <i className="fas fa-images"></i>
                <p>{t('imageArchive', 'gallery.empty')}</p>
                <span>{t('imageArchive', 'gallery.uploadFirst')}</span>
              </div>
            )}
          </div>
        </div>
        
        {notification && (
          <div className={`ba-notification ${notification.type}`}>
            <p>{notification.message}</p>
            <button 
              className="close-button"
              onClick={() => setNotification(null)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billedearkiv;
